import { ResourcesConfig } from 'aws-amplify';

/** Custom OIDC provider name registered on the Cognito user pool (Federate). */
export const FEDERATE_PROVIDER_NAME = 'AmazonFederate';

/**
 * Runtime configuration the app fetches from `/config.json` at startup. The CDK
 * website stack writes this file into the S3 bucket at deploy time with the
 * stage-specific Cognito ids (see WebsiteStack `DeployRuntimeConfig`).
 */
export interface RuntimeConfig {
  readonly stage: string;
  readonly region: string;
  readonly userPoolId: string;
  readonly userPoolClientId: string;
  /** Cognito hosted UI domain, e.g. rme-building-monitoring-beta.auth.us-west-2.amazoncognito.com */
  readonly cognitoDomain: string;
}

/** Fetches `/config.json` (served from the same CloudFront origin). */
export const loadRuntimeConfig = async (): Promise<RuntimeConfig> => {
  const response = await fetch('/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load /config.json: ${response.status}`);
  }
  return await response.json();
};

/**
 * OAuth callback/logout URLs must be a subset of those registered on the Cognito
 * app client (see CognitoStack). We use the current origin so the same build
 * works on any stage domain, plus localhost for local development against beta.
 */
const redirectUrls = (): string[] => {
  const urls = new Set<string>(['http://localhost:3000/']);
  if (typeof window !== 'undefined' && window.location?.origin) {
    urls.add(`${window.location.origin}/`);
  }
  return Array.from(urls);
};

/**
 * Builds the Amplify v6 configuration for a Cognito user pool (no identity
 * pool) from the runtime config. The hosted UI OAuth flow federates to Amazon
 * Federate (Midway) via OIDC. Shape follows the standard Amplify v6
 * `ResourcesConfig.Auth.Cognito.loginWith.oauth` structure.
 */
export const buildCognitoConfig = (config: RuntimeConfig): ResourcesConfig => ({
  Auth: {
    Cognito: {
      userPoolId: config.userPoolId,
      userPoolClientId: config.userPoolClientId,
      loginWith: {
        oauth: {
          domain: config.cognitoDomain,
          scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
          redirectSignIn: redirectUrls(),
          redirectSignOut: redirectUrls(),
          responseType: 'code',
        },
      },
    },
  },
});
